// Unified User Service - switches between API and DynamoDB based on configuration
import dynamoUserService from './dynamoUserService';
import apiUserService from './apiUserService';

// Configuration to determine which service to use
// Environment-based service selection:
// All environments now use API Gateway
const USE_API = true;

// Log configuration only in development
if (process.env.NODE_ENV === 'development') {
  console.log('🔧 User Service Configuration:', {
    USE_API,
    service: USE_API ? 'API' : 'DynamoDB'
  });
}

// Service selector
const getService = () => {
  return USE_API ? apiUserService : dynamoUserService;
};

// Unified interface
export const userService = {
  async getUser(userId) {
    const service = getService();
    if (USE_API) {
      try {
        const result = await service.getUser(userId);
        if (result) {
          return { success: true, data: result };
        } else {
          return { success: false, error: 'User not found' };
        }
      } catch (error) {
        return { success: false, error: error.message };
      }
    } else {
      // Return the full DynamoDB service result {success: true, data: ...}
      return await service.getUserProfile(userId);
    }
  },

  async createUser(userData) {
    const service = getService();
    if (USE_API) {
      return service.createUser(userData);
    } else {
      // Map to DynamoDB service method
      const result = await service.saveUserProfile(userData);
      return result.success ? result.data : null;
    }
  },

  // Save user profile (handles both create and update intelligently)
  async saveUserProfile(userData) {
    const service = getService();
    if (USE_API) {
      try {
        const result = await service.saveUserProfile(userData);
        if (result) {
          return { success: true, data: result };
        } else {
          return { success: false, error: 'No response from server' };
        }
      } catch (error) {
        return { success: false, error: error.message };
      }
    } else {
      // Map to DynamoDB service method
      const result = await service.saveUserProfile(userData);
      return result; // Return the full result object { success: true/false, error?: string }
    }
  },

  async updateUser(userId, updates) {
    const service = getService();
    if (USE_API) {
      return service.updateUser(userId, updates);
    } else {
      // Map to DynamoDB service method
      const result = await service.updateUserProfile(userId, updates);
      return result.success ? result.data : null;
    }
  },

  async getAllUsers() {
    const service = getService();
    if (USE_API) {
      return service.getAllUsers();
    } else {
      // Map to DynamoDB service method with error handling
      try {
        const result = await service.getAllUsers();
        // Ensure result is defined and has expected structure
        if (!result) {
          return { success: false, users: [], error: 'Service returned undefined' };
        }
        return result; // Return the full result object with success and users properties
      } catch (error) {
        return { success: false, users: [], error: error.message };
      }
    }
  },

  async deleteUser(userId) {
    const service = getService();
    if (USE_API) {
      return service.deleteUser(userId);
    } else {
      // Map to DynamoDB service method
      const result = await service.deleteUserProfile(userId);
      return result.success;
    }
  },

  async batchGetUsers(userIds) {
    const service = getService();
    if (USE_API) {
      return service.batchGetUsers(userIds);
    } else {
      // For DynamoDB, make individual calls
      const users = await Promise.all(
        userIds.map(async (userId) => {
          try {
            const result = await service.getUserProfile(userId);
            return result.success ? result.data : null;
          } catch (error) {
            return null;
          }
        })
      );
      return users.filter(user => user !== null);
    }
  },

  async searchUsers(searchTerm) {
    const service = getService();
    if (USE_API) {
      return service.searchUsers(searchTerm);
    } else {
      // For DynamoDB, get all users and filter
      const result = await service.getAllUsers();
      if (!result.success) return [];
      
      const searchLower = searchTerm.toLowerCase();
      return result.data.filter(user => 
        user.name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.firstName?.toLowerCase().includes(searchLower) ||
        user.lastName?.toLowerCase().includes(searchLower)
      );
    }
  }
};

export default userService;
